import { useCallback, useMemo, useState } from 'react';
import { NavMarker } from 'react-rhumb';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { Flex } from 'foundations-components/components/Flex';
import { EmptyState } from 'foundations-components/transitional/components/EmptyState';
import { Button } from 'foundations-components/transitional/components/Button';
import { GenericErrorState } from '../components/GenericErrorState';
import { CreditCardIcon } from 'foundations-assets/icons/components/CreditCardIcon';
import { BankIcon } from 'foundations-assets/icons/components/BankIcon';
import { WalletIcon } from 'foundations-assets/icons/components/WalletIcon';
import { SendIcon } from 'foundations-assets/icons/components/SendIcon';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { PaginatedDataTable, DEFAULT_PAGE_SIZE } from '../components/PaginatedDataTable';
import { StatusLabel } from '../components/StatusLabel';
import { useCommercePaymentsData } from '../hooks/useCommercePaymentsData';
import { useCursorPagination } from '../hooks/useCursorPagination';
import { useWindowSize } from '../hooks/useWindowSize';
import { applyResponsiveColumnWidths } from '../utils/responsiveTableColumns';
import { PAYMENT_METHOD_TYPE, COMMERCE_PAYMENT_STATUS, BANK_PAYMENT_TYPES, getPaymentMethodLabel } from '../utils/typeAdapters';
import { trackInteraction } from '../utils/usageTracker';
import { SendReceiptModal } from '../components/PaymentHistory/SendReceiptModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function AmountCell({
  row
}) {
  return /*#__PURE__*/_jsx("td", {
    children: /*#__PURE__*/_jsx(FormattedIntlCurrency, {
      value: Number(row.amount),
      currencyCode: row.currencyCode
    })
  });
}
function DateCell({
  row
}) {
  return /*#__PURE__*/_jsx("td", {
    children: typeof row.paymentDate === 'number' && /*#__PURE__*/_jsx(FormattedDateTime, {
      value: row.paymentDate,
      format: "ll"
    })
  });
}
function StatusCell({
  row
}) {
  return /*#__PURE__*/_jsx("td", {
    children: /*#__PURE__*/_jsx(StatusLabel, {
      status: row.status.toUpperCase(),
      domain: "commercePayment"
    })
  });
}
function PaymentMethodCell({
  row
}) {
  var _details$type, _details$brand, _details$last;
  const details = row.paymentMethodDetails;
  const typeStr = readUnhandledEnumCase((_details$type = details.type) !== null && _details$type !== void 0 ? _details$type : '');
  const isCard = typeStr === PAYMENT_METHOD_TYPE.CARD;
  const isBank = BANK_PAYMENT_TYPES.has(typeStr);
  const paymentMethodLabel = getPaymentMethodLabel(typeStr, (_details$brand = details.brand) !== null && _details$brand !== void 0 ? _details$brand : undefined, (_details$last = details.last4) !== null && _details$last !== void 0 ? _details$last : undefined);
  return /*#__PURE__*/_jsx("td", {
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "200",
      children: [isCard ? /*#__PURE__*/_jsx(CreditCardIcon, {
        size: "100"
      }) : isBank ? /*#__PURE__*/_jsx(BankIcon, {
        size: "100"
      }) : /*#__PURE__*/_jsx(WalletIcon, {
        size: "100"
      }), paymentMethodLabel]
    })
  });
}
function ActionsCell({
  row
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleSendReceipt = useCallback(() => {
    trackInteraction('send-receipt-clicked');
    setIsModalOpen(true);
  }, []);
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);
  if (row.status.toUpperCase() !== COMMERCE_PAYMENT_STATUS.SUCCEEDED) {
    return /*#__PURE__*/_jsx("td", {});
  }
  return /*#__PURE__*/_jsxs("td", {
    children: [/*#__PURE__*/_jsx(Button, {
      use: "link",
      onClick: handleSendReceipt,
      "data-test-id": "send-receipt-btn",
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "100",
        children: [/*#__PURE__*/_jsx(SendIcon, {
          size: "100"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "paymentHistory.sendReceipt"
        })]
      })
    }), isModalOpen && /*#__PURE__*/_jsx(SendReceiptModal, {
      commercePaymentCrmId: row.objectId,
      onClose: handleCloseModal
    })]
  });
}
const COLUMNS = [{
  columnName: 'amount',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "paymentHistory.columns.amount"
  }),
  CellComponent: AmountCell
}, {
  columnName: 'paymentDate',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "paymentHistory.columns.date"
  }),
  CellComponent: DateCell
}, {
  columnName: 'status',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "paymentHistory.columns.status"
  }),
  CellComponent: StatusCell
}, {
  columnName: 'paymentMethodDetails',
  sortable: false,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "paymentHistory.columns.paymentMethod"
  }),
  CellComponent: PaymentMethodCell
}, {
  columnName: 'actions',
  sortable: false,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "paymentHistory.columns.actions"
  }),
  CellComponent: ActionsCell
}];
const COLUMN_WIDTH_WEIGHTS = [3, 3, 3, 5, 5];
export function PaymentHistoryTable() {
  var _paymentsData$totalRe;
  const [sort, setSort] = useState(undefined);
  const {
    currentOffset,
    currentPage,
    hasPrev,
    handleNextPage,
    handlePrevPage,
    resetPagination
  } = useCursorPagination();
  const {
    width: windowWidth
  } = useWindowSize();
  const columns = useMemo(() => applyResponsiveColumnWidths(COLUMNS, windowWidth, COLUMN_WIDTH_WEIGHTS), [windowWidth]);
  const sortProperty = sort === null || sort === void 0 ? void 0 : sort.columnName;
  const sortDirection = sort ? sort.direction === 1 ? 'ASC' : 'DESC' : undefined;
  const {
    loading: paymentsLoading,
    error: paymentsError,
    data: paymentsData
  } = useCommercePaymentsData(Object.assign({
    pageLength: DEFAULT_PAGE_SIZE
  }, currentOffset !== undefined && {
    offset: currentOffset
  }, sortProperty !== undefined && {
    sortProperty
  }, sortDirection !== undefined && {
    sortDirection
  }));
  const handleSortsChange = nextSorts => {
    setSort(nextSorts[0]);
    resetPagination();
  };
  const sorts = useMemo(() => sort ? [sort] : [], [sort]);
  const hasMore = Boolean(paymentsData === null || paymentsData === void 0 ? void 0 : paymentsData.hasMore);
  const nextOffset = paymentsData === null || paymentsData === void 0 ? void 0 : paymentsData.offset;
  if (paymentsError) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "PAYMENT_HISTORY_LOAD",
      children: /*#__PURE__*/_jsx(GenericErrorState, {
        inline: true
      })
    });
  }
  if ((paymentsData === null || paymentsData === void 0 ? void 0 : paymentsData.results.length) === 0 && !paymentsLoading) {
    return /*#__PURE__*/_jsx(EmptyState, {
      flush: true,
      layout: "vertical",
      primaryContent: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "paymentHistory.emptyStateMessage"
      })
    });
  }
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "PAYMENT_HISTORY_LOAD",
    children: /*#__PURE__*/_jsx("div", {
      "data-test-id": "payment-history-table",
      children: /*#__PURE__*/_jsx(PaginatedDataTable, {
        columns: columns,
        rows: (paymentsData === null || paymentsData === void 0 ? void 0 : paymentsData.results) || [],
        getRowId: row => {
          var _row$objectId;
          return String((_row$objectId = row.objectId) !== null && _row$objectId !== void 0 ? _row$objectId : row.paymentId);
        },
        loading: paymentsLoading,
        currentPage: currentPage,
        hasMore: hasMore,
        hasPrev: hasPrev,
        onNextPage: () => nextOffset && handleNextPage(nextOffset),
        onPrevPage: handlePrevPage,
        sorts: sorts,
        onSortsChange: handleSortsChange,
        totalResultsCount: (_paymentsData$totalRe = paymentsData === null || paymentsData === void 0 ? void 0 : paymentsData.totalResults) !== null && _paymentsData$totalRe !== void 0 ? _paymentsData$totalRe : 0
      })
    })
  });
}